import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

export function useDebouncedSearch<T>(
  searchFn: (query: string) => Promise<T[]>,
  { minLength = 3, delay = 300 }: { minLength?: number; delay?: number } = {}
) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchFnRef = useRef(searchFn);

  useEffect(() => {
    searchFnRef.current = searchFn;
  }, [searchFn]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setQuery(value);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (value.length >= minLength) {
        debounceRef.current = setTimeout(async () => {
          setIsSearching(true);
          const searchResults = await searchFnRef.current(value);
          setResults(searchResults);
          setIsSearching(false);
        }, delay);
      } else {
        setResults([]);
      }
    },
    [minLength, delay]
  );

  const clearResults = useCallback(() => {
    setQuery('');
    setResults([]);
  }, []);

  return { query, results, isSearching, handleChange, clearResults };
}
